import React, { useState, useEffect } from 'react';
import './ProductDetailsPage.css';

const ProductDetails = ({ product, BULK_ENQUIRY_PHONE, setShowBulkEnquiryModal }) => {
  // States for UI tabs and collapsible sections
  const [activeTab, setActiveTab] = useState('description');
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [basicSpecsVisible, setBasicSpecsVisible] = useState(true);
  const [techSpecsVisible, setTechSpecsVisible] = useState(true);
  const [packageDetailsVisible, setPackageDetailsVisible] = useState(false);

  // Handle responsive tabs layout
  useEffect(() => {
    // Function to handle responsive tab layout
    const handleResponsiveTabs = () => {
      const isMobileView = window.innerWidth <= 767;
      const tabContents = document.querySelectorAll('.tab-content-section');

      // Make sure elements exist
      if (tabContents.length === 0) return;

      // Show/hide content based on active tab
      tabContents.forEach((content, index) => {
        if (index === activeTabIndex) {
          content.style.display = 'block';
        } else {
          content.style.display = 'none';
        }
      });
    };

    // Call the function initially
    handleResponsiveTabs();

    // Add resize listener
    window.addEventListener('resize', handleResponsiveTabs);

    return () => {
      window.removeEventListener('resize', handleResponsiveTabs);
    };
  }, [activeTab, activeTabIndex]);

  // Tab handling function with animation
  const handleTabClick = (tabName, index) => {
    setActiveTab(tabName);
    setActiveTabIndex(index);

    // Scroll to tabs on mobile when a tab is clicked
    if (window.innerWidth <= 767) {
      const tabContent = document.querySelector('.tab-content2');
      if (tabContent) {
        // Small delay to ensure layout is updated
        setTimeout(() => {
          tabContent.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  };

  // Toggle package details visibility
  const togglePackageDetails = () => {
    setPackageDetailsVisible(!packageDetailsVisible);
  };

  // Get delivery info based on speed
  const getDeliveryInfo = (deliverySpeed) => {
    switch (deliverySpeed) {
      case 'quick':
        return {
          icon: '⚡',
          label: 'Quick Delivery',
          description: 'Delivered today'
        };
      case 'express':
        return {
          icon: '🚀',
          label: 'Express Delivery',
          description: 'Delivered within hours'
        };
      case 'late':
        return {
          icon: '🐌',
          label: 'Eco Delivery',
          description: 'Delivered in 2-3 days'
        };
      case 'normal':
      default:
        return {
          icon: '🚚',
          label: 'Standard Delivery',
          description: 'Delivered by next day'
        };
    }
  };

  const deliveryInfo = getDeliveryInfo(product.deliverySpeed || 'normal');

  // Build the specifications array based on product type, including custom specs
  const getSpecifications = () => {
    const baseSpecs = [
      { name: "Model", value: product.model || `M${product.id.substring(0, 5)}` },
      { name: "Material", value: product.material || "High-quality composite" },
      { name: "Dimensions", value: product.dimensions || "30cm x 20cm x 10cm" },
      { name: "Weight", value: product.weight || "250g" },
      { name: "Warranty", value: product.warranty || "1 year manufacturer warranty" },
      { name: "Delivery", value: `${deliveryInfo.label} - ${deliveryInfo.description}` }
    ];

    const electronicSpecs = [
      { name: "Microcontroller", value: product.microcontroller },
      { name: "Operating Voltage", value: product.operatingVoltage },
      { name: "Input Voltage (Recommended)", value: product.inputVoltageRecommended },
      { name: "Input Voltage (Limits)", value: product.inputVoltageLimits },
      { name: "Digital I/O Pins", value: product.digitalIOPins },
      { name: "Analog Input Pins", value: product.analogInputPins },
      { name: "PWM Channels", value: product.pwmChannels },
      { name: "Clock Speed", value: product.clockSpeed },
      { name: "Flash Memory", value: product.flashMemory },
      { name: "SRAM", value: product.sram },
      { name: "EEPROM", value: product.eeprom },
      { name: "USB Interface", value: product.usbInterface },
      { name: "Communication Interfaces", value: product.communicationInterfaces },
      { name: "Operating Temperature", value: product.operatingTemperature },
      { name: "Power Consumption", value: product.powerConsumption },
      { name: "LED Indicators", value: product.ledIndicators }
    ];

    // Add custom specifications if available
    const customSpecs = (product.customSpecifications || []).map(spec => ({
      name: spec.title,
      value: spec.value
    }));

    const allSpecs = [...baseSpecs, ...electronicSpecs, ...customSpecs].filter(spec => spec.value);

    return allSpecs;
  };

  const specifications = getSpecifications();

  // Project usages
  const projectUsages = product.projectUsages || [
    "Home renovation projects",
    "Professional construction",
    "DIY crafting",
    "Industrial applications"
  ];

  // Check if product has package points, key features, or applications
  const hasPackagePoints = product.packagePoints && product.packagePoints.length > 0;
  const hasKeyFeatures = product.keyFeatures && product.keyFeatures.length > 0;
  const hasApplications = product.applications && product.applications.length > 0;
  const hasDetailsSection = hasPackagePoints || hasKeyFeatures || hasApplications;

  return (
    <div className="product-detail-info2">
      <div className="modal-sections2">
        {/* Delivery Options Section - Static display without onClick */}
        <div className="delivery-options-box2">
          <div className="delivery-options-title2">
            <span className="delivery-truck-icon2">🚚</span> Delivery Options
          </div>

          <div className="delivery-options-list2">
            <div className="delivery-option-item2">
              <div className="delivery-option-label2">
                <span className="option-icon2">⚡</span>
                <div className="option-details2">
                  <div className="option-name2">Quick Delivery</div>
                  <div className="option-desc2">Delivered today before 4pm</div>
                </div>
              </div>
            </div>

            <div className="delivery-option-item2">
              <div className="delivery-option-label2">
                <span className="option-icon2">🚚</span>
                <div className="option-details2">
                  <div className="option-name2">Standard Delivery</div>
                  <div className="option-desc2">Delivered next day within 4pm</div>
                </div>
              </div>
            </div>

            <div className="delivery-option-item2">
              <div className="delivery-option-label2">
                <span className="option-icon2">📦</span>
                <div className="option-details2">
                  <div className="option-name2">Eco Delivery</div>
                  <div className="option-desc2">Delivered within 2-3 days</div>
                </div>
              </div>
            </div>
          </div>

          <div className="delivery-info-note2">
            Our delivery service is available across Bangalore.
            <a href={`tel:${BULK_ENQUIRY_PHONE}`} className="contact-info-btn2">
              CONTACT DIRECTLY TO GET MORE INFO - {BULK_ENQUIRY_PHONE}
            </a>
          </div>
        </div>

        {/* Tabbed Interface for Product Information */}
        <div className="product-tabs2">
          <div className="tab-header2">
            <button
              className={`tab-btn2 ${activeTab === 'description' ? 'active' : ''}`}
              onClick={() => handleTabClick('description', 0)}
            >
              Description
            </button>
            <button
              className={`tab-btn2 ${activeTab === 'specifications' ? 'active' : ''}`}
              onClick={() => handleTabClick('specifications', 1)}
            >
              Specifications
            </button>
            <button
              className={`tab-btn2 ${activeTab === 'projects' ? 'active' : ''}`}
              onClick={() => handleTabClick('projects', 2)}
            >
              Projects
            </button>
          </div>

          <div className="tab-content2">
            <section
              className={`tab-content-section ${activeTab === 'description' ? 'active' : ''}`}
              style={{ display: activeTabIndex === 0 ? 'block' : 'none' }}
            >
              <p>{product.fullDescription || product.description || "This premium product is designed to meet all your needs with excellent quality and durability. It's a must-have for both professionals and enthusiasts."}</p>
            </section>

            <section
              className={`tab-content-section ${activeTab === 'specifications' ? 'active' : ''}`}
              style={{ display: activeTabIndex === 1 ? 'block' : 'none' }}
            >
              {specifications.length > 0 ? (
                <div className="specs-categories2">
                  {basicSpecsVisible ? (
                    <div className="specs-group2">
                      <div className="specs-group-header2" onClick={() => setBasicSpecsVisible(!basicSpecsVisible)}>
                        <h4>Basic Specifications</h4>
                        <span className="toggle-icon2">{basicSpecsVisible ? '−' : '+'}</span>
                      </div>
                      <div className="specs-group-content2">
                        <table className="specs-table2">
                          <tbody>
                            {specifications.filter(spec =>
                              ['Model', 'Material', 'Dimensions', 'Weight', 'Warranty', 'Delivery'].includes(spec.name)
                            ).map((spec, index) => (
                              <tr key={index}>
                                <td className="spec-name2">{spec.name}</td>
                                <td className="spec-value2">{spec.value}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <div className="specs-group2 collapsed2">
                      <div className="specs-group-header2" onClick={() => setBasicSpecsVisible(!basicSpecsVisible)}>
                        <h4>Basic Specifications</h4>
                        <span className="toggle-icon2">{basicSpecsVisible ? '−' : '+'}</span>
                      </div>
                    </div>
                  )}

                  {techSpecsVisible ? (
                    <div className="specs-group2">
                      <div className="specs-group-header2" onClick={() => setTechSpecsVisible(!techSpecsVisible)}>
                        <h4>Technical Specifications</h4>
                        <span className="toggle-icon2">{techSpecsVisible ? '−' : '+'}</span>
                      </div>
                      <div className="specs-group-content2">
                        <table className="specs-table2">
                          <tbody>
                            {specifications.filter(spec =>
                              !['Model', 'Material', 'Dimensions', 'Weight', 'Warranty', 'Delivery'].includes(spec.name)
                            ).map((spec, index) => (
                              <tr key={index}>
                                <td className="spec-name2">{spec.name}</td>
                                <td className="spec-value2">{spec.value}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <div className="specs-group2 collapsed2">
                      <div className="specs-group-header2" onClick={() => setTechSpecsVisible(!techSpecsVisible)}>
                        <h4>Technical Specifications</h4>
                        <span className="toggle-icon2">{techSpecsVisible ? '−' : '+'}</span>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p>No specifications available for this product.</p>
              )}
            </section>

            <section
              className={`tab-content-section ${activeTab === 'projects' ? 'active' : ''}`}
              style={{ display: activeTabIndex === 2 ? 'block' : 'none' }}
            >
              <ul className="project-list2">
                {projectUsages.map((project, index) => (
                  <li key={index}>{project}</li>
                ))}
              </ul>
            </section>
          </div>
        </div>

        {/* Package Details Section - For showing package points, key features, and applications */}
        {hasDetailsSection && (
          <div className="package-details-section">
            <div className="package-details-header" onClick={togglePackageDetails}>
              <h3>Details</h3>
              <span className="toggle-icon2">{packageDetailsVisible ? '−' : '+'}</span>
            </div>
            {packageDetailsVisible && (
              <div className="package-details-content">
                {/* Package Points */}
                {hasPackagePoints && (
                  <div className="details-subsection">
                    <ul className="package-points-list">
                      {product.packagePoints.map((point, index) => (
                        <li key={index}>
                          <span className="package-point-bullet">•</span>
                          <span className="package-point-text">{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Key Features */}
                {hasKeyFeatures && (
                  <div className="details-subsection">
                    <h4 className="details-subsection-title">Key Features</h4>
                    <ul className="package-points-list">
                      {product.keyFeatures.map((feature, index) => (
                        <li key={index}>
                          <span className="package-point-bullet">•</span>
                          <span className="package-point-text">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Applications */}
                {hasApplications && (
                  <div className="details-subsection">
                    <h4 className="details-subsection-title">Applications</h4>
                    <ul className="package-points-list">
                      {product.applications.map((application, index) => (
                        <li key={index}>
                          <span className="package-point-bullet">•</span>
                          <span className="package-point-text">{application}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Support Section - Moved outside of tabs as requested */}
        <section className="shipping-support-section2">
          <h3>Support & Shipping</h3>
          <div className="shipping-support-grid2">
            <div className="support-item2">
              <div className="support-icon2">🚚</div>
              <div className="support-info2">
                <h4>Same Day Shipping</h4>
                <p>If Ordered within 5 Hours and 25 Minutes</p>
              </div>
            </div>

            <div className="support-item2">
              <div className="support-icon2">👤</div>
              <div className="support-info2">
                <h4>Technical Support & Easy Returns</h4>
                {/* <a href="/support/tickets" className="support-link2">Raise a Ticket</a> */}
              </div>
            </div>

            <div className="support-item2">
              <div className="support-icon2">🕒</div>
              <div className="support-info2">
                <h4>Earn Coins on Every Purchase</h4>
                <p>Redeem for discounts on future orders</p>
              </div>
            </div>

            <div className="support-item2">
              <div className="support-icon2">🔧</div>
              <div className="support-info2">
                <h4>Customized Models Supply</h4>
                <p>Tailored to your specifications</p>
              </div>
            </div>
          </div>
        </section>
        <div className="bulk-enquiry-section2">
          <div className="bulk-enquiry-container2" onClick={() => setShowBulkEnquiryModal(true)}>
            <div className="bulk-enquiry-icon2">
              <span>📦</span>
            </div>
            <div className="bulk-enquiry-text2">
              Bulk Order Enquiry
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;